import { z } from 'zod';
import { IsoDateTime, Uuid } from '../primitives.js';

/**
 * WS-14 — NOTIFICATIONS INBOX. An in-app, SALON-LEVEL ops inbox (no web-push). read_at is SALON-WIDE
 * shared read-state (a salon ops inbox, NOT per-user). Exactly 4 curated kinds.
 *
 * PII discipline: a notification is a SALON-WIDE surface, so its title/body NEVER carry a sealed
 * renter's client identity (e.g. a sealed renter's client NAME) — they reference service/time/staff/
 * product only. entityId is a SOFT reference (the entity may be erased while the notification remains).
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * The curated notification event set:
 *   'low_stock'      — a product crossed its low-stock threshold;
 *   'online_booking' — a NEW booking arrived via the PUBLIC online-booking flow (not staff-created);
 *   'comms_at_cap'   — the salon's messaging allowance hit its cap (overage under auto-top-up, OR a send
 *                      paused/blocked at cap);
 *   'no_show'        — an appointment transitioned to no_show.
 */
export const NotificationKind = z.enum(['low_stock', 'online_booking', 'comms_at_cap', 'no_show']);
export type NotificationKind = z.infer<typeof NotificationKind>;

// ── Notification (operator-facing entity) ──────────────────────────────────────

/**
 * One salon-level notification. read_at null = unread (shared salon-wide read-state). entityType +
 * entityId are an OPTIONAL deep-link; entityId is a soft reference (no FK) so an erased entity never
 * orphans the audit artifact.
 */
export const Notification = z
  .object({
    id: Uuid,
    salonId: Uuid,
    kind: NotificationKind,
    title: z
      .string()
      .describe(
        'denormalised human summary — never a sealed client name (service/time/staff/product)',
      ),
    body: z.string().nullable(),
    entityType: z.enum(['product', 'appointment', 'comms']).nullable(),
    entityId: Uuid.nullable(),
    readAt: IsoDateTime.nullable().describe('null = unread (salon-wide shared read-state)'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Notification' });
export type Notification = z.infer<typeof Notification>;

/** The list response: a page of notifications + the salon's unread count + the total matching count. */
export const NotificationList = z
  .object({
    data: z.array(Notification),
    unreadCount: z.int().min(0),
    total: z.int().min(0),
  })
  .meta({ id: 'NotificationList' });
export type NotificationList = z.infer<typeof NotificationList>;

// ── Query schema (NO .meta — query/param gotcha) ───────────────────────────────

export const ListNotificationsQuery = z.object({
  unreadOnly: z.coerce.boolean().optional(),
  kind: NotificationKind.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListNotificationsQuery = z.infer<typeof ListNotificationsQuery>;
